import {
  useBandContext,
  useCalendarContext,
  useEventContext,
} from "Bands/contexts";
import { dispatchGetEvents } from "Bands/state";
import { EventBlocksType, IEvent, IIconBandSetting } from "Bands/types";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { copy } from "shared/assets";
import { Copy, File } from "shared/assets/icons";
import { Button, Modal } from "shared/components";
import { Spacer } from "shared/layout";
import { Icon } from "shared/styles";
import { IFormOption, ITheme } from "shared/types";
import { StringParam, useQueryParams } from "use-query-params";
import {
  CalendarPicker,
  CalendarView,
  TodayAction,
} from "../calendar/containers";
import { StyledCalendarTools } from "../calendar/styles";
import { CopyBlockForm } from "./CopyBlockForm";
import { ContentWidth } from "shared/styles/constants";

interface Props {
  blockType: EventBlocksType;
  deleteBlockId?: string;
  infoBlockType?: IIconBandSetting;
}

const CopyBlockModal: FC<Props> = ({
  blockType,
  deleteBlockId,
  infoBlockType,
}) => {
  const { bandId } = useBandContext();
  const { event, eventId } = useEventContext();
  const { start, stop } = useCalendarContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setQuery] = useQueryParams({
    month: StringParam,
    year: StringParam,
    view: StringParam,
  });

  const dispatch = useDispatch();

  const [selectedEventBlocks, setSelectedEventBlocks] = useState<IFormOption[]>(
    []
  );

  const [selectedSourceEvent, setSelectedSourceEvent] =
    useState<IEvent | null>(null);

  const handleModalClose = () => {
    setQuery({
      month: undefined,
      year: undefined,
      view: undefined,
    });

    setIsModalOpen(false);
    setSelectedEventBlocks([]);
    setSelectedSourceEvent(null);
  };

  useEffect(() => {
    dispatchGetEvents(dispatch, bandId ?? undefined, start, stop);
  }, [bandId, dispatch, start, stop]);

  const buttonTitle = useMemo(
    () =>
      copy.copyBlock.buttonTitle.replace(
        "%blockTypeButtonCopy",
        infoBlockType
          ? `${infoBlockType?.label}`
          : copy.copyBlock.blockTypeButtonCopies[
              blockType as keyof typeof copy.copyBlock.blockTypeButtonCopies
            ]
      ),
    [blockType, infoBlockType]
  );

  const modalTitle = useMemo(
    () =>
      copy.copyBlock.modalTitle.replace(
        "%blockTypeCopy",
        infoBlockType
          ? `${infoBlockType?.label} Block`
          : copy.copyBlock.blockTypeCopies[
              blockType as keyof typeof copy.copyBlock.blockTypeCopies
            ]
      ),
    [blockType, infoBlockType]
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          title={modalTitle}
          icon={File}
          handleClose={handleModalClose}
          showCloseButton
          width={ContentWidth.wide}
        >
          {!!selectedEventBlocks.length ? (
            <CopyBlockForm
              blockType={blockType}
              sourceEvent={selectedSourceEvent}
              targetEvent={event!}
              selectedEventBlocks={selectedEventBlocks}
              handleModalClose={handleModalClose}
              deleteBlockId={deleteBlockId}
              infoBlockType={infoBlockType}
            />
          ) : (
            <Spacer size="l">
              <StyledCalendarTools>
                <Spacer spaceDirection="horizontal" size="l">
                  <CalendarPicker />
                  <TodayAction />
                </Spacer>
              </StyledCalendarTools>

              <CalendarView
                eventId={eventId!}
                setSelectedEventBlocks={setSelectedEventBlocks}
                setSelectedSourceEvent={setSelectedSourceEvent}
                blockType={blockType}
                infoBlockType={infoBlockType}
              />
            </Spacer>
          )}
        </Modal>
      )}

      <Spacer size="s" spaceDirection="horizontal" align="center">
        <Button
          onClick={() => setIsModalOpen(true)}
          theme={ITheme.secondary}
          size="m"
        >
          <Icon as={Copy} />
          <span>{buttonTitle}</span>
        </Button>
      </Spacer>
    </>
  );
};

export default CopyBlockModal;
